/**
 * MultiChoiceQuiz Adult Renderer
 * Thin rendering component that uses the useMultiChoiceQuiz headless hook for logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QUIZ_OPTION_STATES, QuizImage, QuizSolveDescription, QuizHeader } from '../shared'
import type { MultiChoiceQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useMultiChoiceQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Check, X } from 'lucide-react'

interface MultiChoiceQuizProps {
  quizId: string | number
  quiz: MultiChoiceQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function MultiChoiceQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: MultiChoiceQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit,
  } = useMultiChoiceQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* subtype C일 때 데스크톱에서만 보기 개수만큼 컬럼 표시 */}
      {quiz.subtype === 'C' && (
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (min-width: 768px) {
              #${gridContainerId} {
                grid-template-columns: repeat(${sortedAnswers.length}, minmax(0, 1fr));
              }
            }
          `
        }} />
      )}
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.multiChoice.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      <div
        id={quiz.subtype === 'C' ? gridContainerId : undefined}
        role="listbox"
        aria-label={t('types.multiChoice.selectOption')}
        aria-multiselectable="true"
        className={cn(
          quiz.subtype === 'B' && 'grid grid-cols-1 md:grid-cols-2 gap-3',
          quiz.subtype === 'C' && 'grid grid-cols-1 gap-3',
          (!quiz.subtype || (quiz.subtype !== 'B' && quiz.subtype !== 'C')) && 'space-y-3'
        )}
      >
        {sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key)
          // resolvedCorrectKeySet으로 정답 여부 확인 (correctAnswers를 실제 key로 변환한 Set)
          const isInResolvedCorrectAnswers = resolvedCorrectKeySet.has(answer.key)

          // 선택한 답안의 정오 여부 (corrects 배열 기준)
          const isCorrectByCorrects = correctsBySelectedKey.get(answer.key)

          // 정답보기 모드 (shouldShowCorrectAnswers):
          //   - resolvedCorrectKeySet에 포함된 항목 = 정답 스타일
          //   - 오답 스타일 표시하지 않음
          // 내 답안보기 모드 (!shouldShowCorrectAnswers):
          //   - 선택한 보기 중 corrects=true인 항목: 정답 스타일
          //   - 선택한 보기 중 corrects=false인 항목: 오답 스타일
          const showAsCorrect = isSolved && (
            shouldShowCorrectAnswers
              ? isInResolvedCorrectAnswers // 정답보기: 변환된 correctAnswers 기준
              : isSelected && (isCorrectByCorrects !== undefined ? isCorrectByCorrects : isInResolvedCorrectAnswers) // 내 답안: corrects 우선
          )

          // 오답 스타일 표시 로직:
          // - 정답보기 모드에서는 오답 스타일 숨김
          // - 내 답안보기 모드에서만 선택했지만 정답이 아닌 항목에 오답 스타일
          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect

          return (
            <motion.button
              key={answer.key}
              role="option"
              aria-selected={isSelected}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => handleSelect(answer.key)}
              disabled={isSolved || isViewSolved}
              className={cn(
                'w-full p-4 rounded-xl border-2 text-left transition-all',
                'flex items-center gap-4 text-xl',
                // 기본 상태
                !isSolved && !isSelected && QUIZ_OPTION_STATES.default,
                !isSolved && !isSelected && 'hover:border-blue-400 hover:bg-blue-50',
                // 선택 상태
                !isSolved && isSelected && QUIZ_OPTION_STATES.selected,
                // 정답
                showAsCorrect && QUIZ_OPTION_STATES.correct,
                // 오답
                isWrongSelected && QUIZ_OPTION_STATES.wrong,
                // 비활성화
                isSolved && !showAsCorrect && !isWrongSelected && QUIZ_OPTION_STATES.disabled,
                (isSolved || isViewSolved) && 'cursor-not-allowed'
              )}
            >
              {/* 선택 인디케이터 (check-circle) */}
              <div
                className={cn(
                  'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all',
                  // 기본 (풀이 전, 미선택)
                  !isSelected && !isSolved && 'border-gray-400 bg-white',
                  // 선택 (풀이 전)
                  isSelected && !isSolved && 'border-blue-500 bg-blue-500 shadow-[0_0_0_3px_#eff6ff]',
                  // 정답 스타일
                  showAsCorrect && 'border-green-500 bg-green-500',
                  // 오답 스타일
                  isWrongSelected && 'border-red-500 bg-red-500',
                  // 비활성화 (풀이 후, 정답도 오답도 아닌 항목)
                  isSolved && !showAsCorrect && !isWrongSelected && 'border-gray-300 bg-gray-100'
                )}
              >
                {/* 풀이 전 미선택 상태 - 번호 표시 */}
                {!isSelected && !isSolved && (
                  <span className="text-[10px] font-bold text-gray-400 leading-none">{idx + 1}</span>
                )}
                {/* 풀이 전 선택 상태 */}
                {isSelected && !isSolved && (
                  <Check className="w-3 h-3 text-white" />
                )}
                {/* 정답 표시 */}
                {showAsCorrect && (
                  <Check className="w-3 h-3 text-white" />
                )}
                {/* 오답 표시 */}
                {isWrongSelected && (
                  <X className="w-3 h-3 text-white" />
                )}
                {/* 풀이 후 비활성화 상태 - 번호 표시 */}
                {isSolved && !showAsCorrect && !isWrongSelected && (
                  <span className="text-[10px] font-bold text-gray-400 leading-none">{idx + 1}</span>
                )}
              </div>

              {/* 보기 이미지 */}
              {answer.image && (
                <QuizImage
                  image={answer.image}
                  alt={t('types.multiChoice.optionAlt', { number: idx + 1 })}
                  className="my-0 shrink-0"
                />
              )}

              {/* 보기 내용 */}
              <div className="flex-1 font-medium text-gray-700">
                <KatexText text={answer.answer} />
              </div>
            </motion.button>
          )
        })}
      </div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers ? Array.from(resolvedCorrectKeySet)
            .map(key => sortedAnswers.find(a => a.key === key)?.answer)
            .filter(Boolean)
            .join(', ') : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 - G3: hideExplanation 시 억제 */}
      {isSolved && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult?.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
